/** Einzelspieler-Einrichtung (der Operationsbefehl) und die Lagebeurteilungen der Szenarien. */
import type { setup as en } from '../en/setup';
import type { Translated } from '../types';

export const setup: Translated<typeof en> = {
  'sp.kicker': 'Operationsbefehl · Einzelspieler',
  'sp.title': 'Den Feldzug planen',
  'sp.footer': 'Der Einzelspieler läuft ganz im Browser. Mit der Leertaste jederzeit anhalten.',
  'sp.theatre': 'Kriegsschauplatz',
  'sp.map': 'Karte',
  'sp.map.generated': 'Fiktive Karte (aus dem Startwert erzeugt)',
  'sp.map.hint': 'Eine historische Karte Europas, ganz unten auch ein erfundener Kontinent.',
  'sp.map.meta': '{year} · {w} × {h} Felder · {km} km je Feld',
  'sp.map.neutral': 'Neutrale Länder (schraffiert) sind für alle Armeen gesperrt.',
  'sp.map.preview': 'Karte des Schauplatzes',
  'sp.nation.you': 'Mensch · Ihr Kommando',
  'sp.nation.take': 'Führen',
  'sp.nation.takeHint': 'Diese Nation selbst führen',
  'theatre.poland-1939.desc':
    'Deutschland gegen Polen: der Korridor, die Weichsel und die Bzura — und Ostpreußen abgeschnitten hinter den polnischen Linien.',
  'theatre.east-prussia-1914.desc':
    'Das Deutsche Kaiserreich gegen das Russische Kaiserreich: die Masurischen Seen, die Johannisburger Heide und der Weg nach Tannenberg.',
  'theatre.europe-1939.desc':
    'Der Kontinent am 1. September 1939: Deutschland, Frankreich, Polen, die Sowjetunion, Italien und Großbritannien (über den Ärmelkanal bei Dover erreichbar) — alle anderen Länder neutral.',
  'theatre.moscow-1941.desc':
    'Die Sowjetunion gegen Deutschland vor Moskau: Wjasma, Brjansk und die Moschaisk-Linie, als der Herbstregen einsetzt.',
  'theatre.stalingrad-1942.desc':
    'Die Sowjetunion gegen Deutschland, Rumänien und Italien: die Stadt an der Wolga, der Donbogen und die dünnen rumänischen Flanken.',
  'theatre.normandy-1944.desc':
    'Die Vereinigten Staaten und das Vereinigte Königreich gegen Deutschland: Bocage, Caen und Saint-Lô — und der Pas-de-Calais wartet.',
  'sp.seed': 'Kartennummer',
  'sp.seed.hint': 'Dieselbe Nummer ergibt stets denselben Kriegsschauplatz.',
  'sp.seed.aria': 'Kartennummer',
  'sp.seed.roll': 'Neue Nummer würfeln',
  'sp.speed': 'Spieltempo',
  'sp.season': 'Jahreszeit',
  'sp.belligerents': 'Kriegsparteien',
  'sp.ffa': 'Jeder gegen jeden',
  'sp.customTeams': 'Bündnisse festlegen',
  'sp.alliesNote': 'Verbündete teilen die Aufklärung und siegen gemeinsam.',
  'sp.you': 'Sie — Oberbefehlshaber',
  'sp.youSub': 'Mensch · Nation I',
  'sp.opponent': 'Gegner {num}',
  'sp.opponentSub': 'Computer · {difficulty}',
  'sp.removeOpponent': 'Gegner entfernen',
  'sp.addOpponent': 'Gegner hinzufügen',
  'sp.factionCount': '{n} von 6 Nationen',
  'sp.oneTeam': 'Alle stehen im selben Bündnis — bitte aufteilen.',
  'sp.rules': 'Kampfbestimmungen',
  'sp.dayLimit': 'Frist in Tagen',
  'sp.dayLimit.hint': 'Läuft die Frist ab, siegt, wer die meisten Siegpunkte hält.',
  'sp.decisive': ({ pct }) => `Entscheidender Sieg · ${pct} SP`,
  'sp.decisive.hint': 'Diesen Anteil aller Siegpunkte 24 Stunden lang halten, um den Krieg zu gewinnen.',
  'sp.decisive.aria': 'Anteil für den Sieg',
  'sp.techs': 'Technologie zu Beginn',
  'sp.brief.head': 'GENERALSTAB',
  'sp.brief.no': 'Nr. {n}',
  'sp.brief.stamp': 'Geheim',
  'sp.brief.title': 'OPERATIONSBEFEHL',
  'sp.brief.theatre': 'Schauplatz',
  'sp.brief.season': 'Jahreszeit',
  'sp.brief.belligerents': 'Parteien',
  'sp.brief.belligerentsVal': '{n} (Sie + {ai} KI)',
  'sp.brief.alignment': 'Bündnislage',
  'sp.brief.versus': '{a} gegen {b}',
  'sp.brief.victory': 'Sieg',
  'sp.brief.victoryVal': '{pct} SP · 24 Std.',
  'sp.brief.timeLimit': 'Frist',
  'sp.brief.technology': 'Technologie',
  'sp.brief.tempo': 'Tempo',
  'sp.brief.opposition': 'Gegner',
  'sp.brief.note':
    'X-Zeit ist Tag 1, 06.00 Uhr. Befehle verlassen das Hauptquartier per Fernsprecher und Kurier. Die Kuriere schützen; der Feind liest erbeutete Post. Bündnisse: {teams}.',
  'sp.brief.noTeams': 'keine — jede Nation für sich',
  'sp.brief.approved': 'Genehmigt,',
  'sp.brief.signature': 'v. Dornheim',
  'sp.launching': 'Mobilmachung …',
  'sp.launch': 'Operationen eröffnen',
  'sp.timeout': 'Das Kriegsministerium antwortet nicht — der Einzelspieler-Host ist nicht angelaufen.',

  // ---- Szenarien
  'scn.kicker': 'Historische Lagen · Einzelspieler',
  'scn.title': 'Szenarien',
  'scn.footer':
    'Gekämpft auf historischen Karten Europas, wo es sie gibt; die KI spielt nach denselben Regeln.',
  'scn.inspired': 'Nach dem Vorbild: {what}',
  'scn.situation': 'Lage',
  'scn.forces': 'Kräfte',
  'scn.theatre': 'Schauplatz {size} · {season}',
  'scn.theatreReal': '{name} · {season}',
  'scn.nations': { one: '{n} Nation', other: '{n} Nationen' },
  'scn.dayLimit': 'Frist {n} Tage',
  'scn.victory': 'Sieg bei {pct} SP',
  'scn.everyone': 'Alle Seiten',
  'scn.you': 'Sie',
  'scn.enemy': 'Feind {num}',
  'scn.army': 'Heer ×{x}',
  'scn.reserve': 'Reserve {n} × {unit}',
  'scn.nothingSpecial': 'Regelausstattung',
  'scn.opposition': 'Gegner',
  'scn.asBriefed': 'Laut Lage',
  'scn.asBriefed.hint': 'Jeder Feind so, wie das Szenario ihn vorsieht',
  'scn.seed': 'Schauplatz',
  'scn.take': 'Kommando übernehmen',
  'scn.stamp': 'Streng geheim',
  'scn.select': 'Auswählen',

  'scn.tannenberg.title': 'Das Tannenberg-Protokoll',
  'scn.tannenberg.inspired': 'Tannenberg, August 1914',
  'scn.tannenberg.brief':
    'Der Feind hat in Überzahl die Grenze überschritten. Seine Stäbe geben ihre Befehle per Funk — und verschlüsseln sie nicht. Mithören, jedem Stoß dort begegnen, wohin er zielt, und den Ring schließen.',
  'scn.tannenberg.lesson': 'In der Unterzahl — doch er funkt im Klartext.',
  'scn.rasputitsa.title': 'Rasputiza',
  'scn.rasputitsa.inspired': 'Die Schlammzeit vor Moskau, Oktober 1941',
  'scn.rasputitsa.brief':
    'Oktober 1941. Die Heeresgruppe Mitte stößt mit frischen Panzerkräften auf Moskau vor, stärker als wir. Der Herbstregen setzt ein, und jeder gehaltene Tag macht ihr die Wege schlechter. Raum gegen Zeit tauschen, das Heer erhalten — und zuschlagen, wenn ihre Panzer im Schlamm stecken.',
  'scn.rasputitsa.lesson': 'Halten, bis sich der Boden gegen ihn wendet.',
  'scn.ghost-army.title': 'Die Geisterarmee',
  'scn.ghost-army.inspired': 'Operation Fortitude und die Geisterarmee, 1944',
  'scn.ghost-army.brief':
    'Normandie, Juli 1944. Der Brückenkopf hält, doch die Hecken der Bocage sind eine Mauer. Der Feind hält noch eine ganze Armee bei Calais, wo er die eigentliche Landung erwartet. Fünf Divisionen aus Zeltbahn und Sperrholz stehen bereit; aus der Luft sind sie von echten nicht zu unterscheiden. Ihn dort binden, wo wir nicht sind — dann ausbrechen, wo er schwach ist.',
  'scn.ghost-army.lesson': 'Ihn verteidigen lassen, wo wir nicht sind.',
  'scn.enigma.title': 'Enigma',
  'scn.enigma.inspired': 'Das polnische Chiffrierbüro, September 1939',
  'scn.enigma.brief':
    'September 1939. Deutschland greift von drei Seiten an — aus Pommern, Schlesien und der Slowakei, und aus Ostpreußen im Rücken des Korridors. Seine Armeen führen per Funk über die Enigma, die er für unknackbar hält. Unser Chiffrierbüro hat sie vor Jahren gebrochen. Seine Befehle lesen, jedem Stoß begegnen, bevor er trifft — doch sparsam, damit er nicht merkt, dass seine Post offenliegt.',
  'scn.enigma.lesson': 'Nutzen, was wir lesen, ohne uns zu verraten.',
  'scn.winter-kessel.title': 'Winterkessel',
  'scn.winter-kessel.inspired': 'Operation Uranus, November 1942',
  'scn.winter-kessel.brief':
    'November 1942. Die 6. Armee des Feindes kämpft um die letzten Straßen Stalingrads; ihre Flanken am Don und zwischen den Seen südlich der Stadt halten rumänische Armeen, dünn und schlecht ausgerüstet. Beide Flanken durchbrechen, sich bei Kalatsch treffen und den Ring schließen, bevor der Winter tiefer wird — der Schnee lässt verhungern, wer abgeschnitten ist.',
  'scn.winter-kessel.lesson':
    'Die schwachen Flanken durchbrechen; der Schnee lässt Eingeschlossene verhungern.',
};
